import { OpenSans } from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import { gsap } from "gsap";
import { Sprite, Text, Texture } from "pixi.js";

import { AbstractSymbol } from "../../../../abstracts/elements/AbstractSymbol";
import { AbstractBitmapText } from "../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractView } from "../../../../abstracts/modules/AbstractView";
import { AbstractWinScreen } from "../../../../abstracts/modules/screens/AbstractWinScreen";
import { fitDisplayObjectToWidth } from "../../../../abstracts/utils/GraphicUtils";
import { DDD } from "../../../DDD";
import { DDDFontsNames } from "../../../assetsLoader/DDDAssetsNames";
import { winFontSize } from "../../../config/DDDConfig";

export class DDDWaysWinScreen extends AbstractWinScreen {
    protected waysWinText: Text;
    protected maxWinMultiplier: AbstractBitmapText;
    protected spacingBetweenWinAndWays: number;

    constructor(parentContainer: AbstractView) {
        super(`ApocalypseWaysWinScreen`, parentContainer);
    }

    protected get totalWinTextDefaultYPosition(): number {
        return -40;
    }

    protected get waysWinTextDefaultYPosition(): number {
        return 72;
    }

    protected init(): void {
        super.init();

        this.spacingBetweenWinAndWays = 174;

        this.totalWinText = new AbstractBitmapText({
            text: `0`,
            style: {
                fontName: DDDFontsNames.DEFAULT,
                fontSize: winFontSize,
            },
        });
        this.totalWinText.name = `totalWinText`;
        this.totalWinText.y = this.totalWinTextDefaultYPosition;
        this.totalWinText.alpha = 0.88;
        this.addChild(this.totalWinText);

        this.waysWinText = new Text(``, {
            fontFamily: OpenSans.FAMILY,
            fontSize: `48px`,
            fontWeight: `bold`,
            stroke: `#000000`,
            strokeThickness: 2,
            dropShadow: true,
            dropShadowAlpha: 0.6,
            dropShadowBlur: 1,
            dropShadowColor: 0x000000,
            dropShadowDistance: 2,
            fill: `#ffffff`,
            letterSpacing: 1,
            lineJoin: `round`,
        });
        this.waysWinText.name = `waysWinText`;
        this.waysWinText.y = this.waysWinTextDefaultYPosition;
        this.waysWinText.alpha = 0.88;
        this.addChild(this.waysWinText);

        this.winTextBackground = new Sprite(Texture.from(`DDD/winNumbersBG`));
        this.winTextBackground.name = `winBg`;
        this.winTextBackground.anchor.set(0.5);
        this.winTextBackground.width = 1040;
        this.winTextBackground.height = 448;
        this.winTextBackground.y = 24;

        this.maxWinMultiplier = new AbstractBitmapText({
            text: `19349X`,
            style: {
                fontName: DDDFontsNames.DEFAULT,
                fontSize: winFontSize / 2,
            },
        });
        this.maxWinMultiplier.name = `maxWinMultiplier`;
        this.maxWinMultiplier.y = 50;
    }

    public handleWaysWin(
        symbols: AbstractSymbol[],
        winAmounts: number[],
        waysNo: number
    ): gsap.core.Timeline {
        super.handleWaysWin(symbols, winAmounts, waysNo);

        const tl = gsap.timeline({
            onUpdate: () => {
                fitDisplayObjectToWidth(this.totalWinText, 700);
            },
        });

        const showWinText = gsap.fromTo(
            this.totalWinText,
            { alpha: 0 },
            {
                alpha: 0.88,
                duration: 0.4,
                onStart: () => {
                    this.parentContainer.addChild(this);

                    this.totalWinText.y = this.totalWinTextDefaultYPosition;

                    this.winTextBackground.alpha = 0;
                    this.addChildAt(this.winTextBackground, 0);

                    this.totalWinText.updateValue(winAmounts[0].toFixed(2).toString());

                    this.waysWinText.alpha = 0;

                    if (waysNo !== -1) {
                        this.waysWinText.text = `${waysNo} ${
                            waysNo > 1
                                ? DDD.apiPlugin.translations.translate(`Ways Won`)
                                : DDD.apiPlugin.translations.translate(`Way Won`)
                        }`.toUpperCase();
                        this.waysWinText.x = -this.waysWinText.width / 2;
                    }

                    if (
                        DDD.model.hasXGod ||
                        (DDD.model.gameData.brokeTheBank &&
                            DDD.model.gameData.betWayWins.length === 0)
                    ) {
                        this.totalWinText.y = 7;

                        this.addChild(this.maxWinMultiplier);
                        this.maxWinMultiplier.y = 200;
                    }
                },
                onUpdate: () => {
                    this.winTextBackground.alpha = this.totalWinText.alpha;
                    if (waysNo !== -1) {
                        this.waysWinText.alpha = this.totalWinText.alpha;
                    }

                    if (
                        DDD.model.hasXGod ||
                        (DDD.model.gameData.brokeTheBank &&
                            DDD.model.gameData.betWayWins.length === 0)
                    ) {
                        this.maxWinMultiplier.alpha = this.totalWinText.alpha;
                    }
                },
            }
        );
        tl.add(showWinText, 0);

        const hideWinText = gsap.fromTo(
            this.totalWinText,
            { alpha: 0.88 },
            {
                alpha: 0,
                duration: 0.4,
                ease: `none`,
                onUpdate: () => {
                    this.winTextBackground.alpha = this.totalWinText.alpha;
                    if (waysNo !== -1) {
                        this.waysWinText.alpha = this.totalWinText.alpha;
                    }

                    if (
                        DDD.model.hasXGod ||
                        (DDD.model.gameData.brokeTheBank &&
                            DDD.model.gameData.betWayWins.length === 0)
                    ) {
                        this.maxWinMultiplier.alpha = this.totalWinText.alpha;
                    }
                },
                onComplete: () => {
                    this.parentContainer.removeChild(this);
                    this.removeChild(this.maxWinMultiplier);
                },
            }
        );
        tl.add(hideWinText, 0.6);

        const hideSplashAnimation = gsap.fromTo(
            this.winTextBackground,
            { alpha: 0.88 },
            {
                alpha: 0,
                duration: 0.45,
                onStart: () => {
                    this.addChildAt(this.winTextBackground, 0);
                },
            }
        );
        tl.add(hideSplashAnimation, 0.55);

        return tl;
    }
}
